import { FC } from 'react';
import { TradingOffer } from 'types';

import {
  formatCurrency,
  formatCurrencyDecimals,
  formatNumber,
} from 'utils/formats';
import { useTranslate } from 'hooks/useTranslate';
import { getOfferStatus } from 'modules/trading/utils/getOfferStatus';
import { isShareClassVisible } from 'modules/trading/utils/isShareClassVisible';

import TradingOfferStatusBadge from '../../../components/TradingOfferStatusBadge';
import { getPartnerName } from '../utils/getPartnerName';
import { getShareClassName } from '../utils/getHoldingClassName';
import { useEquityMarketAdminPanel } from '../context/EquityMarketAdminPanelContext';

type EquityMarketAdminPanelOfferItemProps = {
  offer: TradingOffer;
  isSell: boolean;
};

const EquityMarketAdminPanelOfferItem: FC<
  EquityMarketAdminPanelOfferItemProps
> = ({ offer, isSell }) => {
  const { t } = useTranslate();

  const { actions } = useEquityMarketAdminPanel();

  const status = getOfferStatus(offer);
  const partnerName = getPartnerName(offer.partner);
  const shareClassName = getShareClassName(offer.shareClass);

  const totalAmount = offer.sharesCount.total * offer.sharePrice;
  const iconBg = isSell
    ? 'bg-success-dim text-success'
    : 'bg-info-dim text-info';
  const iconName = isSell ? 'ni-wallet-out' : 'ni-wallet-in';

  const formatOfferDescription = () => {
    const priceBlock = `${formatCurrencyDecimals(offer.sharePrice)}/${t(
      'Share'
    ).toLowerCase()}`;
    const totalBlock = `${t('Total')}: ${formatCurrency(totalAmount)}`;
    const partnerBlock = `${isSell ? t('Seller') : t('Buyer')}: ${partnerName}`;

    return `${priceBlock} · ${totalBlock} · ${partnerBlock}`;
  };

  const formatOfferSummary = () => {
    const sharesBlock = `${formatNumber(offer.sharesCount.total)} ${t(
      'Shares'
    ).toLowerCase()}`;
    const shareClassNameBlock = isShareClassVisible()
      ? `· ${shareClassName}`
      : '';

    return `${sharesBlock} ${shareClassNameBlock}`;
  };

  const handleViewOffer = () => {
    actions.handleViewOfferDetail(offer._id);
  };

  return (
    <div
      className="col-12 list-item-oferta doc-item d-flex align-items-center cursor-pointer"
      onClick={handleViewOffer}
      role="button"
      tabIndex={0}
    >
      <div className={`doc-icon ${iconBg}`}>
        <em className={`icon ni ${iconName}`} />
      </div>

      <div className="flex-grow-1">
        <strong className="fs-13px">{formatOfferSummary()}</strong>
        <div className="text-soft fs-12px">{formatOfferDescription()}</div>
      </div>

      <div className="d-flex align-items-center gap-2">
        <TradingOfferStatusBadge status={status} />

        <a
          className="btn btn-icon btn-round btn-trans"
          href="#"
          title={t('ViewDetail')}
          onClick={handleViewOffer}
        >
          <em className="icon ni ni-forward-ios" />
        </a>
      </div>
    </div>
  );
};

export default EquityMarketAdminPanelOfferItem;
